import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import axiosInstance from '../fetchConfig/axiosConfig.js';
import { getBaseURL } from '../utils/config';
import logger from '../utils/logger';

function LinkAuthTest() {
  const [email, setEmail] = useState('');
  const [muniCode, setMuniCode] = useState('TEST');
  const [taxYear, setTaxYear] = useState('2024');
  const [generatedLink, setGeneratedLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleGenerateLink = async () => {
    if (!email) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setGeneratedLink('');

    try {
      const response = await axiosInstance.post('/auth/assessor-link', {
        email,
        muni_code: muniCode,
        tax_year: taxYear,
        expires_in_hours: 72
      });

      if (response.data.success) {
        // Construct the URL on the frontend side using getBaseURL
        const baseUrl = getBaseURL();
        const frontendUrl = baseUrl.replace(':8081', ':3000').replace('https://server.proprietyrealty.com', 'https://proprietyrealty.com').replace('https://staging.proprietyrealty.com', 'https://staging.proprietyrealty.com');
        logger.log('frontendUrl', frontendUrl);
        const accessLink = `${frontendUrl}/analysis?magic_token=${response.data.magic_token}`;
        
        setGeneratedLink(accessLink);
        setSuccess('Secure access link generated successfully!');
      } else {
        setError(response.data.error || 'Failed to generate link');
      }
    } catch (error) {
      logger.error('Error generating link:', error);
      
      // Handle different types of errors
      if (error.response?.status === 401) {
        setError('Authentication required. Please sign in to generate assessor links.');
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.message === 'No session found' || error.message === 'Invalid or expired session') {
        setError('Please sign in to access this feature.');
      } else {
        setError('Failed to generate secure access link. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTestLink = () => {
    if (generatedLink) {
      // Open the link in a new tab
      window.open(generatedLink, '_blank');
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Link-Based Authentication Test
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        This tool allows you to test the link-based authentication system that will be used 
        in assessor notification emails. Generate a secure access link and test how it works.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Generate Secure Access Link
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="assessor@municipality.gov"
              fullWidth
              required
            />
            
            <TextField
              label="Municipality Code"
              value={muniCode}
              onChange={(e) => setMuniCode(e.target.value)}
              placeholder="TEST"
              fullWidth
            />
            
            <TextField
              label="Tax Year"
              value={taxYear}
              onChange={(e) => setTaxYear(e.target.value)}
              placeholder="2024"
              fullWidth
            />
            
            <Button
              variant="contained"
              onClick={handleGenerateLink}
              disabled={loading || !email}
              sx={{ mt: 2 }}
            >
              {loading ? 'Generating...' : 'Generate Secure Link'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {generatedLink && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Generated Secure Access Link
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 2 }}>
              This link will authenticate the user and take them directly to the analysis page 
              with the specified municipality and tax year pre-loaded.
            </Typography>
            
            <Box sx={{ 
              p: 2, 
              bgcolor: 'grey.100', 
              borderRadius: 1, 
              mb: 2,
              wordBreak: 'break-all',
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            }}>
              {generatedLink}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleTestLink}
                color="primary"
              >
                Test Link (Opens in New Tab)
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(generatedLink);
                  setSuccess('Link copied to clipboard!');
                }}
              >
                Copy Link
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <Divider sx={{ my: 3 }} />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            How It Works
          </Typography>
          
          <Typography variant="body2" paragraph>
            <strong>1. Link Generation:</strong> When offers are finalized, the system generates 
            a secure JWT token containing the assessor's email and access permissions.
          </Typography>
          
          <Typography variant="body2" paragraph>
            <strong>2. Email Delivery:</strong> The secure link is included in the assessor 
            notification email, allowing them to access the system without creating an account.
          </Typography>
          
          <Typography variant="body2" paragraph>
            <strong>3. Authentication:</strong> When the user clicks the link, the frontend 
            extracts the token and email from the URL parameters and authenticates them automatically.
          </Typography>
          
          <Typography variant="body2" paragraph>
            <strong>4. Access Control:</strong> Link-authenticated users get limited access 
            to view their municipality's data in town view mode.
          </Typography>
          
          <Typography variant="body2" paragraph>
            <strong>5. Security:</strong> Links expire after 72 hours and can only be used 
            by the intended recipient.
          </Typography>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Testing Instructions
          </Typography>
          
          <Typography variant="body2" component="div">
            <ol>
              <li>Enter an email address (e.g., "assessor@test.gov")</li>
              <li>Optionally modify the municipality code and tax year</li>
              <li>Click "Generate Secure Link"</li>
              <li>Click "Test Link" to see how it works</li>
              <li>Notice that you're automatically authenticated and taken to the analysis page</li>
              <li>The town view mode is automatically enabled for link-authenticated users</li>
            </ol>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default LinkAuthTest; 